/**
 * OmniClinicalBridge Stripe Configuration
 * OmniAlera Technologies Holdings Inc.
 * 
 * Handles Stripe initialization with proper test/live mode detection
 */

import { loadStripe, Stripe } from '@stripe/stripe-js';
import { supabase } from './supabase';

// ═══════════════════════════════════════════════════════════════════════════════
// ENVIRONMENT VARIABLES
// ═══════════════════════════════════════════════════════════════════════════════

const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '';
const STRIPE_MODE = import.meta.env.VITE_STRIPE_MODE || 'test';

// Key type detection
const isTestKey = STRIPE_PUBLISHABLE_KEY.startsWith('pk_test_');
const isLiveKey = STRIPE_PUBLISHABLE_KEY.startsWith('pk_live_');

// ═══════════════════════════════════════════════════════════════════════════════
// TEST CARDS
// ═══════════════════════════════════════════════════════════════════════════════

export const TEST_CARDS = {
  success: '4242 4242 4242 4242',
  decline: '4000 0000 0000 0002',
  requiresAuth: '4000 0025 0000 3155',
  insufficientFunds: '4000 0000 0000 9995',
};

// ═══════════════════════════════════════════════════════════════════════════════
// STRIPE INSTANCE
// ═══════════════════════════════════════════════════════════════════════════════

let stripePromise: Promise<Stripe | null> | null = null;

export const getStripe = (): Promise<Stripe | null> => {
  if (!stripePromise && STRIPE_PUBLISHABLE_KEY) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise || Promise.resolve(null);
};

// ═══════════════════════════════════════════════════════════════════════════════
// PRICE IDs
// ═══════════════════════════════════════════════════════════════════════════════

export const PRICE_IDS = {
  professional: import.meta.env.VITE_STRIPE_PRICE_PROFESSIONAL || '',
  enterprise: import.meta.env.VITE_STRIPE_PRICE_ENTERPRISE || '',
};

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

export const isStripeTestMode = (): boolean => {
  return isTestKey || STRIPE_MODE === 'test';
};

export const isStripeConfigured = (): boolean => {
  return !!(STRIPE_PUBLISHABLE_KEY && (isTestKey || isLiveKey));
};

// ═══════════════════════════════════════════════════════════════════════════════
// CONFIG EXPORTS (for StripeModeIndicator component)
// ═══════════════════════════════════════════════════════════════════════════════

export const getStripeConfig = () => ({
  publishableKey: STRIPE_PUBLISHABLE_KEY,
  mode: STRIPE_MODE as 'test' | 'live',
  isTestMode: isStripeTestMode(),
  isLiveMode: !isStripeTestMode(),
  isConfigured: isStripeConfigured(),
});

export const getStripeModeIndicator = () => ({
  mode: STRIPE_MODE as 'test' | 'live',
  isTest: isStripeTestMode(),
  label: isStripeTestMode() ? 'TEST MODE' : 'LIVE',
  color: isStripeTestMode() ? 'text-orange-700' : 'text-green-700',
  bgColor: isStripeTestMode() ? 'bg-orange-100' : 'bg-green-100',
  showInProduction: false,
});

// ═══════════════════════════════════════════════════════════════════════════════
// CHECKOUT SESSION CREATION
// ═══════════════════════════════════════════════════════════════════════════════

interface CheckoutResponse {
  url?: string;
  sessionId?: string;
  error?: string;
}

export async function createCheckoutSession(
  priceId: string,
  userId?: string,
  userEmail?: string
): Promise<CheckoutResponse> {
  if (!priceId) {
    return { error: 'No price ID provided' };
  }

  if (priceId.includes('placeholder')) {
    return { 
      error: 'Stripe Price IDs not configured. Please create products in Stripe Dashboard.' 
    };
  }

  try {
    const { data: { session } } = await supabase.auth.getSession();
    const authHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
    if (session?.access_token) authHeaders['Authorization'] = `Bearer ${session.access_token}`;
    const response = await fetch('/.netlify/functions/create-checkout', {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({
        priceId,
        userId,
        userEmail,
        successUrl: `${window.location.origin}/pricing?success=true`,
        cancelUrl: `${window.location.origin}/pricing?canceled=true`,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Checkout session error:', error);
    return { 
      error: error instanceof Error ? error.message : 'Failed to create checkout session' 
    };
  }
}

export async function redirectToCheckout(
  priceId: string,
  userId?: string,
  userEmail?: string
): Promise<void> {
  const result = await createCheckoutSession(priceId, userId, userEmail);

  if (result.error) {
    throw new Error(result.error);
  }

  if (result.url) {
    window.location.href = result.url;
  } else if (result.sessionId) {
    const stripe = await getStripe();
    if (stripe) {
      const { error } = await stripe.redirectToCheckout({ sessionId: result.sessionId });
      if (error) {
        throw new Error(error.message || 'Failed to redirect to checkout');
      }
    }
  }
}

export default getStripe;
